import { captureException } from '@sentry/react';
import { useCallback, useState } from 'react';

type CopiedValue = string | null;

type CopyFn = (text: string) => Promise<boolean>;

// Symbol for clipboard focus error to avoid string conflicts
export const CLIPBOARD_FOCUS_REQUIRED = Symbol('CLIPBOARD_FOCUS_REQUIRED');

export function useCopyToClipboard(): [CopiedValue, CopyFn] {
  const [copiedText, setCopiedText] = useState<CopiedValue>(null);

  const copy: CopyFn = useCallback(async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        setCopiedText(text);

        return true;
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;

        textArea.style.position = 'absolute';
        textArea.style.left = '-999999px';

        try {
          document.body.appendChild(textArea);
          textArea.select();

          document.execCommand('copy');
          setCopiedText(text);
          return true;
        } catch (error) {
          captureException(error);

          setCopiedText(null);
          return false;
        } finally {
          textArea.remove();
        }
      }
    } catch (error) {
      captureException(error);

      setCopiedText(null);

      // Check if it's a NotAllowedError (document not focused)
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        throw CLIPBOARD_FOCUS_REQUIRED;
      }

      // Provide more detailed error information for other clipboard failures
      if (error instanceof DOMException) {
        throw new Error(
          `Clipboard API Error (${error.name}): ${error.message}`,
        );
      }

      return false;
    }
  }, []);

  return [copiedText, copy];
}
